import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  IconButton,
  Box,
  Typography,
  FormHelperText,
  Chip
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';

const DynamicField = ({ field, value, onChange, error }) => {
  const { t, i18n } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);

  const currentLang = i18n.language;
  const label = field.label[currentLang] || field.label.en;

  // Voice recognition
  const startVoiceRecognition = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      alert('Voice recognition is not supported in your browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognitionInstance = new SpeechRecognition();
    recognitionInstance.lang = currentLang === 'mr' ? 'mr-IN' : 'en-US';
    recognitionInstance.continuous = false;
    recognitionInstance.interimResults = false;

    recognitionInstance.onstart = () => {
      setIsRecording(true);
    };

    recognitionInstance.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      onChange(value ? `${value} ${transcript}` : transcript);
      setIsRecording(false);
    };

    recognitionInstance.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
    };

    recognitionInstance.onend = () => {
      setIsRecording(false);
    };

    recognitionInstance.start();
    setRecognition(recognitionInstance);
  };

  const stopVoiceRecognition = () => {
    if (recognition) {
      recognition.stop();
      setIsRecording(false);
    }
  };

  // Multi-entry handlers
  const handleMultiEntryChange = (index, newValue) => {
    const newValues = [...value];
    newValues[index] = newValue;
    onChange(newValues);
  };

  const handleAddEntry = () => {
    onChange([...value, '']);
  };

  const handleRemoveEntry = (index) => {
    const newValues = value.filter((_, i) => i !== index);
    onChange(newValues.length > 0 ? newValues : ['']);
  };

  // Render field based on type
  const renderField = () => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              label={label}
              type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              required={field.required}
              error={!!error}
              helperText={error}
            />
            {field.voiceEnabled && (
              <IconButton
                color={isRecording ? 'error' : 'primary'}
                onClick={isRecording ? stopVoiceRecognition : startVoiceRecognition}
              >
                {isRecording ? <StopIcon /> : <MicIcon />}
              </IconButton>
            )}
          </Box>
        );

      case 'textarea':
        return (
          <Box>
            <TextField
              fullWidth
              label={label}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              required={field.required}
              error={!!error}
              helperText={error}
              multiline
              rows={4}
            />
            {field.voiceEnabled && (
              <Box mt={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isRecording ? <StopIcon /> : <MicIcon />}
                  color={isRecording ? 'error' : 'primary'}
                  onClick={isRecording ? stopVoiceRecognition : startVoiceRecognition}
                >
                  {isRecording ? t('survey.stopRecording') : t('survey.startRecording')}
                </Button>
              </Box>
            )}
          </Box>
        );

      case 'number':
        return (
          <TextField
            fullWidth
            label={label}
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            error={!!error}
            helperText={error}
          />
        );

      case 'dropdown':
        return (
          <FormControl fullWidth error={!!error}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              label={label}
              required={field.required}
            >
              <MenuItem value="">
                <em>Select...</em>
              </MenuItem>
              {field.options?.map((option, index) => (
                <MenuItem key={index} value={option.en}>
                  {option[currentLang] || option.en}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        );

      case 'yesno':
        return (
          <FormControl error={!!error}>
            <Typography variant="subtitle2" gutterBottom>
              {label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <RadioGroup
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio />} label={t('common.yes')} />
              <FormControlLabel value="no" control={<Radio />} label={t('common.no')} />
            </RadioGroup>
            {error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        );

      case 'multientry':
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            {(value || ['']).map((entry, index) => (
              <Box key={index} display="flex" gap={1} mb={1}>
                <TextField
                  fullWidth
                  placeholder={`Entry ${index + 1}`}
                  value={entry}
                  onChange={(e) => handleMultiEntryChange(index, e.target.value)}
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveEntry(index)}
                  disabled={value.length === 1}
                >
                  <RemoveIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddEntry}
            >
              {t('survey.addEntry')}
            </Button>
            {error && <FormHelperText error>{error}</FormHelperText>}
          </Box>
        );

      case 'image':
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
            >
              {t('survey.uploadImage')}
              <input
                type="file"
                hidden
                accept="image/*"
                capture="environment"
                multiple
                onChange={(e) => onChange(e.target.files)}
              />
            </Button>
            {value && value.length > 0 && (
              <Box mt={1}>
                <Chip label={`${value.length} file(s) selected`} color="primary" size="small" />
              </Box>
            )}
            {error && <FormHelperText error>{error}</FormHelperText>}
          </Box>
        );

      default:
        return (
          <TextField
            fullWidth
            label={label}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
            error={!!error}
            helperText={error}
          />
        );
    }
  };

  return <Box>{renderField()}</Box>;
};

export default DynamicField;
